import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Calculator, ChevronDown, Menu, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { LANDING_SERVICES } from "@/components/landing/services";

/**
 * The firm's own site still owns who-we-are; this app owns anything to do with
 * pricing. So the "Services" menu points at the in-app service pages rather
 * than back out to the website, which is what lets somebody compare all three
 * services without ever leaving the calculator.
 */
const NAV_ITEMS = [
  { label: "Home", href: "https://www.t-tite.co.za/", testId: "site-nav-home-link" },
  { label: "About Us", href: "https://www.t-tite.co.za/about-us/", testId: "site-nav-about-link" },
  {
    label: "Our Leadership",
    href: "https://www.t-tite.co.za/our-leadership/",
    testId: "site-nav-leadership-link",
  },
  {
    label: "Contact Us",
    href: "https://www.t-tite.co.za/contact-us/",
    testId: "site-nav-contact-link",
  },
];

export const SiteHeader = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onCalculator = location.pathname === "/" || location.pathname === "/pricing-calculator";
  const onService = LANDING_SERVICES.some((item) => item.path === location.pathname);

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b border-border bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80"
    >
      <div className="mx-auto flex h-[72px] max-w-6xl items-center justify-between gap-4 px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          data-testid="site-header-logo-link"
          className="flex shrink-0 items-center gap-3"
          aria-label="T-TITE Registered Accountants and Auditors — pricing calculator"
        >
          <img
            src="/ttite-logo.png"
            alt="T-TITE Registered Accountants and Auditors"
            className="h-11 w-auto"
            width={183}
            height={44}
          />
        </Link>

        <nav
          data-testid="site-header-nav"
          className="hidden items-center gap-7 text-sm font-medium text-muted-foreground lg:flex"
        >
          {NAV_ITEMS.map((item) => (
            <a
              key={item.label}
              href={item.href}
              data-testid={item.testId}
              className="transition-colors duration-150 hover:text-primary"
            >
              {item.label}
            </a>
          ))}

          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="site-nav-services-trigger"
              className={`inline-flex items-center gap-1.5 transition-colors duration-150 hover:text-primary ${
                onService ? "font-semibold text-primary" : ""
              }`}
            >
              Our Services
              <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[280px] bg-white">
              {LANDING_SERVICES.map(({ slug, path, label, blurb, icon: Icon, accent }) => (
                <DropdownMenuItem key={slug} asChild className="cursor-pointer py-2.5">
                  <Link to={path} data-testid={`site-nav-service-${slug}`}>
                    <span className="flex items-start gap-3">
                      <span
                        className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                        style={{ backgroundColor: `${accent}1a`, color: accent }}
                      >
                        <Icon className="h-4 w-4" aria-hidden="true" />
                      </span>
                      <span>
                        <span className="block text-sm font-semibold text-foreground">{label}</span>
                        <span className="mt-0.5 block text-xs leading-snug text-muted-foreground">
                          {blurb}
                        </span>
                      </span>
                    </span>
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link
            to="/pricing-calculator"
            data-testid="site-nav-calculator-link"
            className={`transition-colors duration-150 hover:text-primary ${
              onCalculator ? "font-semibold text-primary" : ""
            }`}
          >
            Pricing Calculator
          </Link>
        </nav>

        <div className="flex items-center gap-2">
          <a
            href="tel:+27817882993"
            data-testid="site-header-phone-link"
            className="hidden items-center gap-2 text-sm font-semibold text-primary md:inline-flex"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            081 788 2993
          </a>
          <Button
            asChild
            data-testid="site-header-get-quote-button"
            className="hidden sm:inline-flex"
          >
            <Link to="/pricing-calculator">Get a Quote</Link>
          </Button>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="lg:hidden"
                data-testid="site-header-mobile-menu-button"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[86vw] max-w-sm overflow-y-auto bg-white">
              <div className="mt-2 flex flex-col gap-1">
                <img
                  src="/ttite-logo.png"
                  alt="T-TITE"
                  className="mb-6 h-12 w-auto self-start"
                />

                <p className="px-3 pb-1 text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
                  Priced here
                </p>
                {LANDING_SERVICES.map(({ slug, path, label, icon: Icon, accent }) => (
                  <Link
                    key={slug}
                    to={path}
                    onClick={() => setOpen(false)}
                    data-testid={`site-nav-service-${slug}-link-mobile`}
                    className="flex items-center gap-3 rounded-lg px-3 py-3 text-[15px] font-medium text-foreground transition-colors duration-150 hover:bg-secondary hover:text-primary"
                  >
                    <span
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                      style={{ backgroundColor: `${accent}1a`, color: accent }}
                    >
                      <Icon className="h-4 w-4" aria-hidden="true" />
                    </span>
                    {label}
                  </Link>
                ))}
                <Link
                  to="/pricing-calculator"
                  onClick={() => setOpen(false)}
                  data-testid="site-nav-calculator-link-mobile"
                  className="flex items-center gap-3 rounded-lg px-3 py-3 text-[15px] font-semibold text-primary transition-colors duration-150 hover:bg-secondary"
                >
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[color:var(--tt-teal-mist)] text-primary">
                    <Calculator className="h-4 w-4" aria-hidden="true" />
                  </span>
                  Pricing calculator
                </Link>

                <div className="my-3 border-t border-border" />

                {NAV_ITEMS.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    data-testid={`${item.testId}-mobile`}
                    className="rounded-lg px-3 py-3 text-[15px] font-medium text-muted-foreground transition-colors duration-150 hover:bg-secondary hover:text-primary"
                  >
                    {item.label}
                  </a>
                ))}

                <Button asChild className="mt-4" onClick={() => setOpen(false)}>
                  <Link to="/pricing-calculator">Get a Quote</Link>
                </Button>
                <a
                  href="tel:+27817882993"
                  className="mt-4 inline-flex items-center gap-2 px-3 text-sm font-semibold text-primary"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  081 788 2993
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
